import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { LogoText } from "@styling/images";

export const NAVBAR_HEIGHT = 64;

const NavbarContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.background};
  border-bottom: 1px solid ${colors.border};
  height: ${NAVBAR_HEIGHT}px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  flex-shrink: 0;
`;

const MenuButton = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    color: ${colors.text};
  }
`;

const MenuIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  span {
    width: 100%;
    height: 2px;
    background-color: ${colors.text};
    border-radius: 2px;
    transition: all 0.3s;
  }
`;

const NavbarLogoSlot = styled.div<{ $hideOnMobile: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  @media (max-width: 768px) {
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? "none" : "flex")};
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  max-width: 100%;
  min-width: 0;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img`
  height: 32px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;
  display: block;
`;

const isStaging = import.meta.env.VITE_ENVIRONMENT === "staging";

const PageStagingPill = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1102;
  padding: 2px ${spacing.x1}px;
  border-radius: 0 0 0 ${borderRadius.small}px;
  background-color: ${colors.warning};
  color: ${colors.warningText};
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  pointer-events: none;
`;

export const StagingCorner = () => {
  const { t } = useTranslation();
  if (!isStaging) {
    return null;
  }
  return <PageStagingPill>{t("navbar.staging")}</PageStagingPill>;
};

const CenterSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const RightSection = styled.div<{ $marginLeftAuto: boolean; $hideOnMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  flex-shrink: 0;
  margin-left: ${({ $marginLeftAuto }) => ($marginLeftAuto ? "auto" : 0)};
  @media (max-width: 768px) {
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? "none" : "flex")};
    gap: ${spacing.x1}px;
  }
`;

export const DropdownArrow = styled.span<{ $isOpen: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid ${colors.text};
  transition: transform 0.2s;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

export const AppLogo = ({
  homeTo,
  onClick,
}: {
  homeTo: string;
  onClick?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <LogoContainer to={homeTo} onClick={onClick}>
      <LogoImage src={LogoText} alt={t("login.logoAlt")} />
    </LogoContainer>
  );
};

interface NavbarProps {
  onMenuClick?: () => void;
  homeTo?: string;
  center?: React.ReactNode;
  children?: React.ReactNode;
}

export const Navbar = ({ onMenuClick, homeTo = "/", center, children }: NavbarProps) => {
  const { t } = useTranslation();
  const marginLeftAuto = !center;
  const hideChromeOnMobile = !!onMenuClick;

  return (
    <NavbarContainer>
      <LeftSection>
        {onMenuClick && (
          <MenuButton onClick={onMenuClick} aria-label={t("navbar.toggleMenu")}>
            <MenuIcon>
              <span></span>
              <span></span>
              <span></span>
            </MenuIcon>
          </MenuButton>
        )}
        <NavbarLogoSlot $hideOnMobile={hideChromeOnMobile}>
          <AppLogo homeTo={homeTo} />
        </NavbarLogoSlot>
      </LeftSection>
      {center && <CenterSection>{center}</CenterSection>}
      {children && (
        <RightSection
          $marginLeftAuto={marginLeftAuto}
          $hideOnMobile={hideChromeOnMobile}
        >
          {children}
        </RightSection>
      )}
    </NavbarContainer>
  );
};

